import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { CycleBilling } from "@/components/billing/CycleBilling";
import { NotInCycle } from "@/components/billing/NotInCycle";
import { LeakageList, PayoutList } from "@/components/billing/PayoutList";
import { ProviderRateCards } from "@/components/billing/ProviderRateCards";
import { RateTable } from "@/components/billing/RateTable";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "cycle", label: "Monthly cycle" },
  { key: "payouts", label: "Payouts" },
  { key: "rates", label: "Rate table" },
  { key: "provider-rates", label: "Provider rates" },
  { key: "leakage", label: "Leakage" },
];

function previousMonth() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function EvaluationBillingPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const requestedMonth = /^\d{4}-\d{2}$/.test(searchParams.get("month") || "") ? searchParams.get("month") : "";
  const highlight = searchParams.get("highlight") || "";
  const [tab, setTab] = useState("cycle");
  const [month, setMonth] = useState(requestedMonth || previousMonth);
  const [rates, setRates] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [rateResponse, cycleResponse] = await Promise.all([api.get("/evaluation-billing/rates"), api.get("/evaluation-billing/cycles", { params: { month } })]);
      setRates(rateResponse.data);
      setOverview(cycleResponse.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = (message) => {
    setNotice(message);
    load();
  };

  return (
    <div className="app-shell" data-testid="evaluation-billing-shell">
      <AppSidebar activeItem="Evaluation billing" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page compliance-page" data-testid="evaluation-billing-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">FINANCE</p>
              <h1>Evaluation billing</h1>
              <p className="page-subtitle">Monthly cycle: providers submit by 11:59 p.m. ET on the 1st, clients are billed by the 2nd, providers are paid on the 7th of the month after the submission deadline. Fees are visible to the CEO and Finance only.</p>
            </div>
            <label className="billing-month-picker">Cycle month<input type="month" value={month} data-testid="billing-month-input" onChange={(e) => setMonth(e.target.value)} /></label>
          </section>
          <nav className="data-admin-tabs pricing-tabs" data-testid="billing-tabs">
            {TABS.map((item) => <button key={item.key} type="button" className={tab === item.key ? "active" : ""} data-testid={`billing-tab-${item.key}`} onClick={() => setTab(item.key)}>{item.label}</button>)}
          </nav>
          {error && <p className="form-message error-message" data-testid="billing-error">{error}</p>}
          {notice && <p className="form-message success-message" data-testid="billing-notice">{notice}</p>}
          {!overview && !error && <p className="projection-loading list-loading" data-testid="billing-loading">Loading…</p>}
          {overview && <NotInCycle lines={overview.not_in_cycle} highlight={highlight} onChanged={changed} onError={setError} />}
          {overview && tab === "cycle" && <CycleBilling overview={overview} month={month} rateItems={rates?.items || []} highlight={highlight} canMove={user?.role === "system_administrator"} onChanged={changed} onError={setError} onGoToRates={() => setTab("rates")} />}
          {overview && tab === "payouts" && <PayoutList overview={overview} onChanged={changed} onError={setError} />}
          {rates && tab === "rates" && <RateTable data={rates} onChanged={changed} onError={setError} />}
          {rates && tab === "provider-rates" && <ProviderRateCards data={rates} onChanged={changed} onError={setError} />}
          {overview && tab === "leakage" && <LeakageList leakage={overview.leakage} />}
        </main>
      </div>
    </div>
  );
}
